import { useState } from "react";
import { api, IMG } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Home, Lock, CheckCircle2, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function HomeValue() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.address.trim()) {
      toast.error("Please share your name, email, and the property address.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/avm/value", form);
      setResult(res.data);
      if (res.data?.estimate?.price) {
        toast.success("Your estimate is ready");
      } else {
        toast.message("Got it — We'll follow up personally");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (n) => (typeof n === "number" ? `$${Math.round(n).toLocaleString()}` : "—");

  return (
    <div data-testid="home-value-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img src={IMG.interior1} alt="Home value" className="absolute inset-0 w-full h-full object-cover img-warm" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-black/65" />
        <div className="relative h-full container-edge flex items-end pb-16">
          <div className="text-white max-w-3xl">
            <p className="overline text-[#e8c79a]">Instant home value</p>
            <h1 className="hero-headline mt-4 text-5xl md:text-6xl lg:text-7xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
              What's your home worth today?
            </h1>
            <p className="mt-6 text-lg text-white/90 max-w-xl font-light">
              Get an instant LA market estimate — backed by real comps and refined by my local
              insight. Most addresses return in under a minute.
            </p>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline">How it works</p>
            <h2 className="mt-4 text-3xl md:text-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
              A real number, in real time.
            </h2>
            <ul className="mt-8 space-y-6 text-soft">
              <li className="flex gap-4"><span className="text-sand text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>01</span><span>Tell me a bit about you and the property.</span></li>
              <li className="flex gap-4"><span className="text-sand text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>02</span><span>We'll pull a fresh market-backed estimate using nearby comps.</span></li>
              <li className="flex gap-4"><span className="text-sand text-2xl" style={{ fontFamily: "Playfair Display, serif" }}>03</span><span>Get a refined valuation from me with the local context the algorithm can't see.</span></li>
            </ul>
            <p className="mt-10 text-xs text-soft leading-relaxed max-w-md">
              The estimate shown is generated by our market data partner and is meant as a starting
              point — not an appraisal. We'll follow up with a personalized valuation.
            </p>
          </div>

          <div className="lg:col-span-7">
            {result ? (
              <div className="bg-paper border border-soft p-10 md:p-12" data-testid="avm-result">
                <CheckCircle2 size={36} className="text-sand" strokeWidth={1.25} />
                <p className="overline mt-6">Your estimate</p>
                {result.estimate?.price ? (
                  <>
                    <h2 className="mt-3 text-5xl md:text-6xl" style={{ fontFamily: "Playfair Display, serif" }} data-testid="avm-price">
                      {fmt(result.estimate.price)}
                    </h2>
                    {result.estimate.price_range_low && result.estimate.price_range_high && (
                      <p className="mt-3 text-soft" data-testid="avm-range">
                        Range: {fmt(result.estimate.price_range_low)} – {fmt(result.estimate.price_range_high)}
                      </p>
                    )}
                    <p className="mt-2 text-sm text-soft break-words">{result.estimate.address}</p>
                    <p className="mt-8 text-sm text-soft leading-relaxed">
                      This is a starting point based on market data. We'll follow up with a refined
                      valuation that accounts for upgrades, condition, and the nuances of your
                      specific block.
                    </p>
                  </>
                ) : (
                  <>
                    <h2 className="mt-3 text-3xl md:text-4xl" style={{ fontFamily: "Playfair Display, serif" }}>
                      Thanks — we've got your request.
                    </h2>
                    <p className="mt-4 text-soft">
                      {result.message || "We'll reach out personally with a custom valuation."}
                    </p>
                  </>
                )}
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/contact" className="btn-primary" data-testid="avm-contact-cta">Get a refined valuation</Link>
                  <button
                    type="button"
                    onClick={() => { setResult(null); setForm({ name: "", email: "", phone: "", address: "" }); }}
                    className="btn-outline"
                    data-testid="avm-new-btn"
                  >
                    Estimate another home
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="bg-paper border border-soft p-8 md:p-12" data-testid="avm-form">
                <div className="flex items-start gap-4 mb-8">
                  <Lock size={26} className="text-sand shrink-0 mt-1" strokeWidth={1.25} />
                  <div>
                    <p className="overline">Free · No obligation</p>
                    <h3 className="mt-2 text-2xl md:text-3xl" style={{ fontFamily: "Playfair Display, serif" }}>
                      Tell us about the home.
                    </h3>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label className="overline">Your name *</label>
                    <input className="input-editorial mt-2" value={form.name} onChange={update("name")} placeholder="Full name" data-testid="avm-name" required />
                  </div>
                  <div>
                    <label className="overline">Email *</label>
                    <input type="email" className="input-editorial mt-2" value={form.email} onChange={update("email")} placeholder="you@email.com" data-testid="avm-email" required />
                  </div>
                  <div>
                    <label className="overline">Phone (optional)</label>
                    <input type="tel" className="input-editorial mt-2" value={form.phone} onChange={update("phone")} placeholder="(555) 555-5555" data-testid="avm-phone" />
                  </div>
                  <div>
                    <label className="overline">Property address *</label>
                    <input className="input-editorial mt-2" value={form.address} onChange={update("address")} placeholder="123 Main St, Beverly Hills, CA 90210" data-testid="avm-address" required />
                  </div>
                </div>
                <button type="submit" disabled={submitting} className="btn-primary mt-10 w-full md:w-auto disabled:opacity-50" data-testid="avm-submit">
                  {submitting ? (<><Loader2 size={16} className="animate-spin mr-2" /> Calculating…</>) : (<><Home size={16} className="mr-2" /> Show me my estimate <ArrowUpRight size={14} className="ml-2" /></>)}
                </button>
                <p className="mt-6 text-xs text-soft">
                  We'll receive your details and follow up. We never share your information.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
